﻿import * as eventTypes from '../../../map/utils/eventTypes';
import * as layerTypes from '../../../map/utils/layerTypes';
import { fromExtent } from 'ol/geom/Polygon';
import { Chart, PieController, ArcElement, Tooltip, Legend, LineController, LineElement, PointElement, CategoryScale, LinearScale, Filler, BarController, BarElement, BubbleController } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { applyFilter } from './ruleEvaluator';
import { formatNumber, formatPercent, getFkText } from './chartFormats';
import GridChartRenderer from './gridChartRenderer';

Chart.register(PieController, ArcElement, Tooltip, Legend, ChartDataLabels, LineController, LineElement, PointElement, CategoryScale, LinearScale, Filler, BarController, BarElement, BubbleController);

const VISUAL_ONLY_FIELDS = new Set(['title', 'backgroundColor', 'fontColor', 'titleFontSize', 'labelFontSize', 'valueFontSize', 'cardsPerRow']);
const PANEL_HOST_ID = 'js-page-content';

export default class ChartControl {
    constructor(rootUrl, chartsContainer, resourceHandler, layerSettingsHandler, classId, mapId, mapControl) {
        this.rootUrl = rootUrl;
        this.chartsContainer = chartsContainer;
        this.resourceHandler = resourceHandler;
        this.layerSettingsHandler = layerSettingsHandler;
        this.classId = classId;
        this.mapId = mapId;
        this.mapControl = mapControl;
        this.chartLayers = {};
        this.chartInstances = {};
        this._gridRenderers = {};
        this._lastDataHash = {};
        this._parsedConditions = {};
        this._chartFingerprints = {};
        this._anchorLeft = 10;
        this._anchorTop = 10;
        this._anchorInitialized = false;
        this._pendingAnchorPanels = new Set();
        this.lastLeft = this._anchorLeft;
        this.lastTop = this._anchorTop;
        this._nextZ = 501;
        this._chartTypes = this._createChartTypeRegistry();
        this._ensureStyles();
        this._init();
    }

    _createChartTypeRegistry() {
        return {
            Card: {
                defaultWidth: 100,
                defaultHeight: 100,
                init: (layer, chartId) => this._initCardChart(layer, chartId),
                calc: (chart, layer, features) => this._calculateCardChartStatistics(chart, layer, features),
                softEdit: (chart, chartId) => this._softEditCardChart(chart, chartId)
            },
            Pie: {
                defaultWidth: 300,
                defaultHeight: 300,
                init: (layer, chartId) => this._initPieChart(layer, chartId),
                calc: (chart, layer, features) => this._calculatePieChartStatistics(chart, layer, features),
                softEdit: (chart, chartId) => this._softEditPieChart(chart, chartId)
            },
            Line: {
                defaultWidth: 400,
                defaultHeight: 250,
                init: (layer, chartId) => this._initCartesianChart(layer, chartId, 'Line'),
                calc: (chart, layer, features) => this._calculateCartesianChartStatistics(chart, layer, features, 'Line'),
                softEdit: (chart, chartId) => this._softEditCartesianChart(chart, chartId)
            },
            Bar: {
                defaultWidth: 400,
                defaultHeight: 250,
                init: (layer, chartId) => this._initCartesianChart(layer, chartId, 'Bar'),
                calc: (chart, layer, features) => this._calculateCartesianChartStatistics(chart, layer, features, 'Bar'),
                softEdit: (chart, chartId) => this._softEditCartesianChart(chart, chartId)
            },
            Bubble: {
                defaultWidth: 400,
                defaultHeight: 300,
                init: (layer, chartId) => this._initBubbleChart(layer, chartId),
                calc: (chart, layer, features) => this._calculateBubbleChartStatistics(chart, layer, features),
                softEdit: (chart, chartId) => this._softEditBubbleChart(chart, chartId)
            },
            Grid: {
                defaultWidth: 500,
                defaultHeight: 320,
                init: (layer, chartId) => this._initGridChart(layer, chartId),
                calc: (chart, layer, features) => this._calculateGridChartStatistics(chart, layer, features),
                softEdit: (chart, chartId) => this._softEditGridChart(chart, chartId)
            }
        };
    }

    _init() {
        this._addEventHandlers();
        this._observeHostResize();
        this._checkAndInitCharts(this.layerSettingsHandler.layers);
    }

    _getHost() {
        let host = document.getElementById(PANEL_HOST_ID);
        if (!host) return null;
        if (getComputedStyle(host).position === 'static') {
            host.style.position = 'relative';
        }
        return host;
    }

    _ensureAnchor() {
        if (this._anchorInitialized) return;
        let host = this._getHost();
        if (!host) return;
        let zoomEl = host.querySelector('.zoom-out');
        if (!zoomEl) return;
        const gap = 3;
        let hostRect = host.getBoundingClientRect();
        let zoomRect = zoomEl.getBoundingClientRect();
        this._anchorLeft = Math.round(zoomRect.left - hostRect.left);
        this._anchorTop = Math.round(zoomRect.bottom - hostRect.top + gap);
        this.lastLeft = this._anchorLeft;
        this.lastTop = this._anchorTop;
        this._anchorInitialized = true;
    }

    _resolvePendingAnchor() {
        if (this._anchorInitialized) return;
        if (this._pendingAnchorPanels.size === 0) {
            this._ensureAnchor();
            return;
        }
        const prevLeft = this._anchorLeft;
        const prevTop = this._anchorTop;
        this._ensureAnchor();
        if (!this._anchorInitialized) return;
        const dx = this._anchorLeft - prevLeft;
        const dy = this._anchorTop - prevTop;
        this._pendingAnchorPanels.forEach(chartId => {
            if (this._getSavedPosition(chartId)) return;
            const panel = document.getElementById(chartId);
            if (!panel) return;
            panel.style.left = (panel.offsetLeft + dx) + 'px';
            panel.style.top = (panel.offsetTop + dy) + 'px';
        });
        this._pendingAnchorPanels.clear();
    }

    _observeHostResize() {
        let host = this._getHost();
        if (!host || typeof ResizeObserver === 'undefined') return;
        this._hostResizeObserver = new ResizeObserver(() => {
            host.querySelectorAll('.xp-chart-panel').forEach(panel => {
                let clamped = this._clampPosition(
                    panel.offsetLeft, panel.offsetTop, panel.offsetWidth, panel.offsetHeight
                );
                panel.style.left = clamped.left + 'px';
                panel.style.top = clamped.top + 'px';
            });
        });
        this._hostResizeObserver.observe(host);
    }

    _checkAndInitCharts(layers) {
        layers.forEach(layer => {
            if (layer.Type == layerTypes.GROUP && layer.Items != null) {
                this._checkAndInitCharts(layer.Items);
            } else {
                this._initCharts(layer);
            }
        });
    }

    _initCharts(layer) {
        if (layer.Charts != null) {
            let ids = Object.keys(layer.Charts);

            if (ids != null && ids.length > 0) {
                this.chartLayers[layer.Id] = layer;
                ids.forEach(id => {
                    this._initChart(layer, id);
                });
            }
        }
    }

    _editChart(layer, chartId) {
        let chart = layer.Charts[chartId];
        let panel = document.getElementById(chartId);
        if (panel) {
            panel.style.background = chart.backgroundColor || '#ffffff';
            panel.style.color = chart.fontColor || '#000000';
            let titleEl = panel.querySelector('.xp-chart-panel-title');
            if (titleEl) titleEl.textContent = chart.title || '';
        }
        let spec = this._chartTypes[chart.type];
        if (!spec) return;

        let prevFp = this._chartFingerprints[chartId];
        let nextFp = this._getStructuralFingerprint(chart);
        let canSoftEdit = prevFp != null && prevFp === nextFp && (chart.type === 'Card' || this.chartInstances[chartId] != null);

        if (canSoftEdit) {
            spec.softEdit(chart, chartId);
        } else {
            spec.init(layer, chartId);
        }
        this._chartFingerprints[chartId] = nextFp;
    }

    _getStructuralFingerprint(chart) {
        let keys = Object.keys(chart).filter(k => !VISUAL_ONLY_FIELDS.has(k)).sort();
        return keys.map(k => k + '=' + JSON.stringify(chart[k])).join('|');
    }

    _getHostBounds() {
        let host = this._getHost();
        if (host) return { width: host.clientWidth, height: host.clientHeight };
        return { width: window.innerWidth, height: window.innerHeight };
    }

    _incrementChartPositions(chart) {
        const pixelWidth = parseInt(this._getChartWidth(chart), 10);
        const pixelHeight = parseInt(this._getChartHeight(chart), 10);

        const gap = 2;
        const availHeight = this._getHostBounds().height;

        if (this.lastTop + pixelHeight + gap > availHeight) {
            this.lastTop = this._anchorTop;
            this.lastLeft = this.lastLeft + pixelWidth + gap;
        } else {
            this.lastTop = this.lastTop + pixelHeight + gap;
        }
    }

    _findFreeSlot(width, height) {
        const gap = 2;
        const maxIter = 100;
        const availHeight = this._getHostBounds().height;
        let top = this.lastTop;
        let left = this.lastLeft;

        for (let i = 0; i < maxIter; i++) {
            if (!this._rectCollidesWithPanels(left, top, width, height)) {
                return { top: top, left: left };
            }
            top += height + gap;
            if (top + height > availHeight) {
                top = this._anchorTop;
                left += width + gap;
            }
        }
        return { top: top, left: left };
    }

    _rectCollidesWithPanels(left, top, width, height) {
        let panels = document.querySelectorAll('.xp-chart-panel');
        for (let i = 0; i < panels.length; i++) {
            let p = panels[i];
            let pLeft = p.offsetLeft;
            let pTop = p.offsetTop;
            let pWidth = p.offsetWidth;
            let pHeight = p.offsetHeight;
            if (left < pLeft + pWidth && left + width > pLeft &&
                top < pTop + pHeight && top + height > pTop) {
                return true;
            }
        }
        return false;
    }

    _initChart(layer, chartId) {
        let chart = layer.Charts[chartId];
        let width = parseInt(this._getChartWidth(chart), 10);
        let height = parseInt(this._getChartHeight(chart), 10);

        let saved = this._getSavedPosition(chartId);
        let top, left;

        if (saved) {
            top = saved.top;
            left = saved.left;
            if (saved.width) width = saved.width;
            if (saved.height) height = saved.height;
            let clamped = this._clampPosition(left, top, width, height);
            top = clamped.top;
            left = clamped.left;
        } else {
            this._ensureAnchor();
            let slot = this._findFreeSlot(width, height);
            top = slot.top;
            left = slot.left;
            this.lastTop = top;
            this.lastLeft = left;
            this._incrementChartPositions(chart);
            if (!this._anchorInitialized) {
                this._pendingAnchorPanels.add(chartId);
            }
        }

        let panel = document.createElement('div');
        panel.id = chartId;
        panel.className = 'xp-chart-panel';
        let bgColor = chart.backgroundColor || '#ffffff';
        let ftColor = chart.fontColor || '#000000';
        panel.style.cssText = 'top:' + top + 'px;left:' + left + 'px;width:' + width + 'px;height:' + height + 'px;background:' + bgColor + ';color:' + ftColor + ';';

        panel.innerHTML =
            '<div class="xp-chart-panel-dragbar"><span class="xp-chart-panel-title"></span></div>' +
            '<div id="chart-' + chartId + '" class="xp-map-chart xp-chart-panel-body"></div>' +
            '<div class="xp-chart-panel-resize">&#8690;</div>';

        let titleEl = panel.querySelector('.xp-chart-panel-title');
        titleEl.textContent = chart.title || '';
        let titleFontSize = parseInt(chart.titleFontSize, 10);
        if (titleFontSize) titleEl.style.fontSize = titleFontSize + 'px';

        (this._getHost() || document.body).appendChild(panel);

        panel.addEventListener('mousedown', () => this._bringToFront(panel));
        this._makeDraggable(panel, chartId);
        this._makeResizable(panel, chartId);

        let spec = this._chartTypes[chart.type];
        if (spec) spec.init(layer, chartId);
        this._chartFingerprints[chartId] = this._getStructuralFingerprint(chart);
    }

    _softEditTitleElement(chart, chartId) {
        let panel = document.getElementById(chartId);
        if (!panel) return;
        let titleEl = panel.querySelector('.xp-chart-panel-title');
        if (!titleEl) return;
        let titleFontSize = parseInt(chart.titleFontSize, 10);
        if (titleFontSize) titleEl.style.fontSize = titleFontSize + 'px';
    }

    _softEditCardChart(chart, chartId) {
        let valueFontSize = parseInt(chart.valueFontSize, 10) || 20;
        let labelFontSize = parseInt(chart.titleFontSize, 10) || 10;

        let valueEl = document.getElementById('card-chart-content-' + chartId);
        if (valueEl) valueEl.style.fontSize = valueFontSize + 'px';

        let panel = document.getElementById(chartId);
        if (panel) {
            panel.querySelectorAll('.xp-chart-card-tile-value').forEach(el => {
                el.style.fontSize = valueFontSize + 'px';
            });
            panel.querySelectorAll('.xp-chart-card-tile-label').forEach(el => {
                el.style.fontSize = labelFontSize + 'px';
            });
        }

        let grid = document.getElementById('card-chart-grid-' + chartId);
        if (grid) this._applyCardsPerRow(grid, chart.cardsPerRow);

        this._softEditTitleElement(chart, chartId);
    }

    _softEditPieChart(chart, chartId) {
        this._softEditTitleElement(chart, chartId);
        let instance = this.chartInstances[chartId];
        if (!instance) return;
        let labelFontSize = parseInt(chart.labelFontSize, 10) || 10;
        let fontColor = chart.fontColor || '#000000';
        let opts = instance.options;
        opts.plugins.legend.labels.color = fontColor;
        opts.plugins.legend.labels.font.size = labelFontSize;
        opts.plugins.datalabels.font.size = labelFontSize;
        instance.update();
    }

    _softEditCartesianChart(chart, chartId) {
        this._softEditTitleElement(chart, chartId);
        let instance = this.chartInstances[chartId];
        if (!instance) return;
        let labelFontSize = parseInt(chart.labelFontSize, 10) || 10;
        let fontColor = chart.fontColor || '#000000';
        let opts = instance.options;
        opts.plugins.legend.labels.color = fontColor;
        opts.plugins.legend.labels.font.size = labelFontSize;
        opts.scales.x.ticks.color = fontColor;
        opts.scales.x.ticks.font.size = labelFontSize;
        opts.scales.y.ticks.color = fontColor;
        opts.scales.y.ticks.font.size = labelFontSize;
        instance.update();
    }

    _softEditBubbleChart(chart, chartId) {
        this._softEditTitleElement(chart, chartId);
        let instance = this.chartInstances[chartId];
        if (!instance) return;
        let labelFontSize = parseInt(chart.labelFontSize, 10) || 10;
        let fontColor = chart.fontColor || '#000000';
        let opts = instance.options;
        opts.plugins.legend.labels.color = fontColor;
        opts.plugins.legend.labels.font.size = labelFontSize;
        opts.scales.x.ticks.color = fontColor;
        opts.scales.x.ticks.font.size = labelFontSize;
        opts.scales.x.title.color = fontColor;
        opts.scales.x.title.font.size = labelFontSize;
        opts.scales.y.ticks.color = fontColor;
        opts.scales.y.ticks.font.size = labelFontSize;
        opts.scales.y.title.color = fontColor;
        opts.scales.y.title.font.size = labelFontSize;
        instance.update();
    }

    _softEditGridChart(chart, chartId) {
        this._softEditTitleElement(chart, chartId);
    }

    _initGridChart(layer, chartId) {
        let chart = layer.Charts[chartId];
        let container = document.getElementById('chart-' + chartId);
        if (!container) return;
        container.textContent = '';

        let host = document.createElement('div');
        host.className = 'xp-grid-chart-host';
        host.id = 'grid-chart-host-' + chartId;
        container.appendChild(host);

        this._gridRenderers[chartId] = new GridChartRenderer(host, chart, {
            onRowClick: (feature) => this._onGridRowClick(feature, layer),
            onRowEnter: (feature) => this._onGridRowEnter(feature, layer),
            onRowLeave: (feature, oldState) => this._onGridRowLeave(feature, layer, oldState),
            onColumnResize: () => this._onGridColumnResize(layer, chartId),
            getResource: (k) => this.resourceHandler.get(k)
        });

        this._addGridExportButton(chartId, chart);
    }

    _addGridExportButton(chartId, chart) {
        let panel = document.getElementById(chartId);
        if (!panel) return;
        let dragbar = panel.querySelector('.xp-chart-panel-dragbar');
        if (!dragbar || dragbar.querySelector('.xp-grid-chart-export-btn')) return;

        let btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'xp-grid-chart-export-btn';
        btn.title = this.resourceHandler.get('GridChartExport');
        btn.innerHTML =
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
                '<path d="M12 3 V15"/><path d="M7 10 L12 15 L17 10"/><path d="M5 19 H19"/>' +
            '</svg>';
        btn.addEventListener('mousedown', (e) => e.stopPropagation());
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            this._exportGridCsv(chartId, chart);
        });
        dragbar.appendChild(btn);
    }

    _exportGridCsv(chartId, chart) {
        let renderer = this._gridRenderers[chartId];
        if (!renderer) return;
        let csv = renderer.toCsv();
        let blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
        let baseName = (chart && chart.title) ? chart.title : 'grid';
        let safeName = String(baseName).replace(/[\\/:*?"<>|]+/g, '').replace(/\s+/g, '_').slice(0, 80) || 'grid';
        let url = URL.createObjectURL(blob);
        let a = document.createElement('a');
        a.href = url;
        a.download = safeName + '.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    _onGridRowClick(feature, layer) {
        if (!feature || !this.mapControl) return;
        let fid = feature.getId ? feature.getId() : null;
        if (fid != null) {
            this.mapControl.selectFeature(layer.Id, fid, true, false, true);
        }
    }

    _onGridRowEnter(feature, layer) {
        if (!feature || !this.mapControl) return null;
        let fid = feature.getId ? feature.getId() : null;
        if (fid == null) return null;
        return this.mapControl.highlightFeature(layer.Id, fid);
    }

    _onGridRowLeave(feature, layer, oldState) {
        if (!feature || !this.mapControl) return;
        let fid = feature.getId ? feature.getId() : null;
        if (fid == null) return;
        this.mapControl.unhighlightFeature(layer.Id, fid, oldState == null ? 0 : oldState);
    }

    _onGridColumnResize(layer, chartId) {
        let chart = layer.Charts && layer.Charts[chartId];
        if (!chart) return;
        if (this.layerSettingsHandler && typeof this.layerSettingsHandler._setChartToStorage === 'function') {
            this.layerSettingsHandler._setChartToStorage(layer.Id, chart);
        }
        this._chartFingerprints[chartId] = this._getStructuralFingerprint(chart);
    }

    _calculateGridChartStatistics(chart, layer, rawFeatures) {
        let features = this._extractFeatures(rawFeatures, chart);
        let renderer = this._gridRenderers[chart.id];
        if (!renderer) return;

        renderer.updateChart(chart);

        if (!layer.VisibleByDefault) {
            renderer.setRows([]);
            return;
        }

        let rowLimit = parseInt(chart.rowLimit, 10);
        let rows = features;
        if (rowLimit && rowLimit > 0 && rows.length > rowLimit) {
            rows = rows.slice(0, rowLimit);
        }
        renderer.setRows(rows);
    }

    _initCardChart(layer, chartId) {
        let chart = layer.Charts[chartId];

        let container = document.getElementById('chart-' + chartId);
        container.textContent = '';

        let card = document.createElement('div');
        card.className = 'xp-chart-card-container';

        if (chart.categoryField) {
            card.classList.add('is-grid');
            let grid = document.createElement('div');
            grid.className = 'xp-chart-card-grid';
            grid.id = 'card-chart-grid-' + chartId;
            card.appendChild(grid);
        } else {
            let valueFontSize = chart.valueFontSize || '20';
            let wrapper = document.createElement('div');
            wrapper.className = 'xp-chart-card-content-wrapper';
            let content = document.createElement('p');
            content.className = 'card-chart-content';
            content.style.fontSize = valueFontSize + 'px';
            content.id = 'card-chart-content-' + chartId;
            wrapper.appendChild(content);
            card.appendChild(wrapper);
        }

        container.appendChild(card);
    }

    _initPieChart(layer, chartId) {
        let chart = layer.Charts[chartId];
        let container = document.getElementById('chart-' + chartId);
        let labelFontSize = parseInt(chart.labelFontSize, 10) || 10;
        let fontColor = chart.fontColor || '#000000';
        let labelType = chart.labelType || 'percentage';

        this._renderTitledCanvas(container, chartId, 'pie');

        if (this.chartInstances[chartId]) {
            this.chartInstances[chartId].destroy();
        }

        let canvas = document.getElementById('pie-canvas-' + chartId);
        this.chartInstances[chartId] = new Chart(canvas, {
            type: 'pie',
            data: {
                labels: [],
                datasets: [{
                    data: [],
                    backgroundColor: this._getChartColors(chart)
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                layout: {
                    padding: 2
                },
                plugins: {
                    legend: {
                        display: true,
                        position: 'bottom',
                        labels: {
                            font: { size: labelFontSize },
                            color: fontColor,
                            boxWidth: 10,
                            padding: 4
                        }
                    },
                    tooltip: {
                        callbacks: {
                            label: (context) => {
                                let total = context.dataset.data.reduce((a, b) => a + b, 0);
                                let value = context.parsed;
                                return context.label + ': ' + formatNumber(value) + ' (' + formatPercent(value, total) + ')';
                            }
                        }
                    },
                    datalabels: {
                        color: '#fff',
                        font: {
                            size: labelFontSize,
                            weight: 'bold'
                        },
                        formatter: (value, context) => {
                            let total = context.dataset.data.reduce((a, b) => a + b, 0);
                            if (total === 0) return '';
                            if (labelType === 'value') {
                                return formatNumber(value);
                            }
                            return formatPercent(value, total);
                        },
                        display: (context) => {
                            let total = context.dataset.data.reduce((a, b) => a + b, 0);
                            if (total === 0) return false;
                            let pct = (context.dataset.data[context.dataIndex] / total) * 100;
                            return pct > 5;
                        }
                    }
                }
            }
        });
    }

    _getChartColors(chart) {
        let palettes = {
            default: [
                '#EF706B', '#6BBF73', '#FFE566', '#5DA0E5', '#FCAA5A',
                '#AB6CC3', '#4DC8D9', '#E4619A', '#D2D966', '#F5A7C0',
                '#52ADA3', '#C8B8E8', '#BFA08E', '#D96060', '#8DD49A'
            ],
            pastel: [
                '#F2838E', '#7ED4A0', '#FFF176', '#8FA7D6', '#F4A87C',
                '#C49DC7', '#8EE5F5', '#F28DE6', '#D9F59A', '#FACDD4',
                '#8DC9C4', '#E4D4FF', '#D4A870', '#C47070', '#BDFFD6'
            ],
            vivid: [
                '#e6194b', '#3cb44b', '#ffe119', '#4363d8', '#f58231',
                '#911eb4', '#42d4f4', '#f032e6', '#bfef45', '#fabed4',
                '#469990', '#dcbeff', '#9A6324', '#800000', '#aaffc3'
            ]
        };

        let key = chart.colorPalette || 'default';
        if (key === 'custom' && Array.isArray(chart.customColors) && chart.customColors.length > 0) {
            return chart.customColors;
        }
        return palettes[key] || palettes.default;
    }

    _getColorForValue(value, chart) {
        let colors = this._getChartColors(chart);
        let hash = 0;
        let str = String(value);
        for (let i = 0; i < str.length; i++) {
            hash = ((hash << 5) - hash) + str.charCodeAt(i);
            hash |= 0;
        }
        return colors[Math.abs(hash) % colors.length];
    }

    _initCartesianChart(layer, chartId, type) {
        let chart = layer.Charts[chartId];
        let container = document.getElementById('chart-' + chartId);
        let titleFontSize = parseInt(chart.titleFontSize, 10) || 12;
        let labelFontSize = parseInt(chart.labelFontSize, 10) || 10;
        let fontColor = chart.fontColor || '#000000';
        let prefix = type === 'Bar' ? 'bar' : 'line';

        this._renderTitledCanvas(container, chartId, prefix);

        if (this.chartInstances[chartId]) {
            this.chartInstances[chartId].destroy();
        }

        let datasets = [];
        if (chart.seriesMode !== 'split') {
            let series = chart.series || [];
            datasets = series.map((s, i) => {
                let color = s.color || this._getChartColors(chart)[i] || '#008FFB';
                return this._buildCartesianDataset(type, {
                    label: s.name || ('Series ' + (i + 1)),
                    color: color,
                    chart: chart,
                    seriesIndex: i
                });
            });
        }

        let stacked = (type === 'Bar' && !!chart.stacked) || (type === 'Line' && !!chart.stackedArea);

        let canvas = document.getElementById(prefix + '-canvas-' + chartId);
        this.chartInstances[chartId] = new Chart(canvas, {
            type: type === 'Bar' ? 'bar' : 'line',
            data: {
                labels: [],
                datasets: datasets
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: {
                    mode: 'index',
                    intersect: false
                },
                plugins: {
                    legend: {
                        display: true,
                        position: 'bottom',
                        labels: {
                            font: { size: labelFontSize },
                            color: fontColor,
                            boxWidth: 12,
                            padding: 4
                        }
                    },
                    tooltip: {
                        mode: 'index',
                        intersect: false
                    },
                    datalabels: {
                        display: false
                    }
                },
                scales: {
                    x: {
                        stacked: type === 'Bar' && !!chart.stacked,
                        ticks: {
                            font: { size: labelFontSize },
                            color: fontColor,
                            maxRotation: 45,
                            autoSkip: true,
                            maxTicksLimit: 20
                        },
                        grid: {
                            display: false
                        }
                    },
                    y: {
                        stacked: stacked,
                        ticks: {
                            font: { size: labelFontSize },
                            color: fontColor
                        },
                        beginAtZero: true
                    }
                }
            }
        });
    }

    _buildCartesianDataset(type, opts) {
        let { label, color, chart, seriesIndex } = opts;
        if (type === 'Bar') {
            return {
                label: label,
                data: [],
                backgroundColor: color,
                borderColor: color,
                borderWidth: 1
            };
        }

        let fill = false;
        if (chart.stackedArea) {
            fill = seriesIndex === 0 ? 'origin' : '-1';
        } else if (chart.areaFill) {
            fill = 'origin';
        }
        return {
            label: label,
            data: [],
            borderColor: color,
            backgroundColor: this._withAlpha(color, '40'),
            borderWidth: 2,
            pointRadius: 3,
            pointHoverRadius: 5,
            tension: 0.3,
            fill: fill
        };
    }

    _calculateCartesianChartStatistics(chart, layer, rawFeatures, type) {
        let features = this._extractFeatures(rawFeatures, chart);
        let xField = chart.xField;
        let sortOrder = chart.sortOrder || 'asc';

        if (chart.seriesMode === 'split') {
            this._calculateCartesianSplit(chart, layer, features, xField, sortOrder, type);
        } else {
            this._calculateCartesianManual(chart, layer, features, xField, sortOrder);
        }
    }

    _calculateCartesianManual(chart, layer, features, xField, sortOrder) {
        let series = chart.series || [];

        if (!layer.VisibleByDefault || features.length === 0 || series.length === 0) {
            this._updateCartesianDatasets(chart.id, [], series.map(() => []));
            return;
        }

        let grouped = this._groupByX(features, xField, sortOrder);
        let datasetsData = series.map(s => grouped.xKeys.map(xKey =>
            this._aggregateValues(grouped.featuresByX[xKey], s.yField, s.aggregation)
        ));

        this._updateCartesianDatasets(chart.id, grouped.labels, datasetsData);
    }

    _calculateCartesianSplit(chart, layer, features, xField, sortOrder, type) {
        let splitField = chart.splitByField;
        let valueField = chart.splitValueField;
        let aggregation = chart.splitAggregation || 'COUNT';

        if (!layer.VisibleByDefault || features.length === 0) {
            this._updateCartesianReplace(chart.id, [], []);
            return;
        }

        let grouped = this._groupByXAndSplit(features, xField, splitField, sortOrder);

        let datasets = grouped.splitKeys.map((splitKey, i) => {
            let color = this._getColorForValue(splitKey, chart);
            let data = grouped.xKeys.map(xKey => {
                let groupFeatures = (grouped.groups[xKey] && grouped.groups[xKey][splitKey]) || [];
                return this._aggregateValues(groupFeatures, valueField, aggregation);
            });
            return Object.assign(
                this._buildCartesianDataset(type, {
                    label: grouped.splitLabelMap[splitKey],
                    color: color,
                    chart: chart,
                    seriesIndex: i
                }),
                { data: data }
            );
        });

        this._updateCartesianReplace(chart.id, grouped.labels, datasets);
    }

    _groupByX(features, xField, sortOrder) {
        let featuresByX = {};
        let labelMap = {};

        features.forEach(feature => {
            let xKey = feature.get(xField);
            if (xKey == null) xKey = 'N/A';
            let xStr = String(xKey);

            if (labelMap[xStr] == null) {
                let fkText = this._getFkText(feature, xField);
                labelMap[xStr] = fkText != null ? fkText : xStr;
            }

            if (featuresByX[xStr] == null) featuresByX[xStr] = [];
            featuresByX[xStr].push(feature);
        });

        let xKeys = this._sortKeys(Object.keys(featuresByX), sortOrder);
        return {
            xKeys: xKeys,
            labels: xKeys.map(k => labelMap[k]),
            featuresByX: featuresByX
        };
    }

    _groupByXAndSplit(features, xField, splitField, sortOrder) {
        let xValues = {};
        let xLabelMap = {};
        let splitValues = {};
        let splitLabelMap = {};
        let groups = {};

        features.forEach(feature => {
            let xKey = feature.get(xField);
            if (xKey == null) xKey = 'N/A';
            let xStr = String(xKey);

            if (xLabelMap[xStr] == null) {
                let fkText = this._getFkText(feature, xField);
                xLabelMap[xStr] = fkText != null ? fkText : xStr;
            }

            let splitKey = feature.get(splitField);
            if (splitKey == null) splitKey = 'N/A';
            let splitStr = String(splitKey);

            if (splitLabelMap[splitStr] == null) {
                let fkText = this._getFkText(feature, splitField);
                splitLabelMap[splitStr] = fkText != null ? fkText : splitStr;
            }

            splitValues[splitStr] = true;
            xValues[xStr] = true;

            if (groups[xStr] == null) groups[xStr] = {};
            if (groups[xStr][splitStr] == null) groups[xStr][splitStr] = [];
            groups[xStr][splitStr].push(feature);
        });

        let xKeys = this._sortKeys(Object.keys(xValues), sortOrder);
        let splitKeys = this._sortKeys(Object.keys(splitValues), 'asc');

        return {
            xKeys: xKeys,
            labels: xKeys.map(k => xLabelMap[k]),
            splitKeys: splitKeys,
            splitLabelMap: splitLabelMap,
            groups: groups
        };
    }

    _sortKeys(keys, sortOrder) {
        let numeric = keys.length > 0 && keys.every(k => k !== 'N/A' && k !== '' && Number.isFinite(Number(k)));
        let sorted = keys.slice().sort(numeric
            ? (a, b) => Number(a) - Number(b)
            : (a, b) => a < b ? -1 : a > b ? 1 : 0);
        return sortOrder === 'desc' ? sorted.reverse() : sorted;
    }

    _updateCartesianDatasets(chartId, labels, datasetsData) {
        let instance = this.chartInstances[chartId];
        if (!instance) return;
        instance.data.labels = labels;
        datasetsData.forEach((data, i) => {
            if (instance.data.datasets[i]) {
                instance.data.datasets[i].data = data;
            }
        });
        instance.update();
    }

    _updateCartesianReplace(chartId, labels, datasets) {
        let instance = this.chartInstances[chartId];
        if (!instance) return;
        instance.data.labels = labels;
        instance.data.datasets = datasets;
        instance.update();
    }

    _renderTitledCanvas(container, chartId, prefix) {
        container.textContent = '';

        let wrapper = document.createElement('div');
        wrapper.className = 'xp-chart-pie-canvas-wrapper';
        let canvas = document.createElement('canvas');
        canvas.id = prefix + '-canvas-' + chartId;
        wrapper.appendChild(canvas);

        container.appendChild(wrapper);
    }

    _withAlpha(hex, alphaHex) {
        if (typeof hex !== 'string' || hex[0] !== '#') return hex;
        let h = hex.slice(1);
        if (h.length === 3) {
            h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
        }
        if (h.length !== 6) return hex;
        return '#' + h + alphaHex;
    }

    _aggregateValues(features, valueField, aggregation) {
        aggregation = aggregation || 'COUNT';
        if (!valueField || valueField.trim() === '') {
            return features.length;
        }
        let items = features.map(f => f.get(valueField));
        switch (aggregation) {
            case 'SUM': return this._getSumResult(items);
            case 'AVERAGE': return this._getAverageResult(items);
            case 'MIN': return this._getMinResult(items);
            case 'MAX': return this._getMaxResult(items);
            case 'MEDIAN': return this._getMedianResult(items);
            case 'COUNT_UNIQUE': return this._getUniqueCountResult(items);
            case 'COUNT': return this._getNonNullCount(items);
            default: return this._getNonNullCount(items);
        }
    }

    _getNonNullCount(items) {
        if (!Array.isArray(items)) return 0;
        let n = 0;
        for (let i = 0; i < items.length; i++) if (items[i] != null) n++;
        return n;
    }

    _initBubbleChart(layer, chartId) {
        let chart = layer.Charts[chartId];
        let container = document.getElementById('chart-' + chartId);
        let labelFontSize = parseInt(chart.labelFontSize, 10) || 10;
        let fontColor = chart.fontColor || '#000000';

        this._renderTitledCanvas(container, chartId, 'bubble');

        if (this.chartInstances[chartId]) {
            this.chartInstances[chartId].destroy();
        }

        let canvas = document.getElementById('bubble-canvas-' + chartId);
        this.chartInstances[chartId] = new Chart(canvas, {
            type: 'bubble',
            data: {
                datasets: []
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        display: !!chart.splitByField,
                        position: 'bottom',
                        labels: {
                            font: { size: labelFontSize },
                            color: fontColor,
                            boxWidth: 12,
                            padding: 4
                        }
                    },
                    tooltip: {
                        callbacks: {
                            label: (context) => {
                                let p = context.raw;
                                return context.dataset.label + ': (' + formatNumber(p.x) + ', ' + formatNumber(p.y) + ', ' + formatNumber(p._rawSize) + ')';
                            }
                        }
                    },
                    datalabels: {
                        display: false
                    }
                },
                scales: {
                    x: {
                        ticks: {
                            font: { size: labelFontSize },
                            color: fontColor
                        },
                        title: {
                            display: !!chart.xField,
                            text: chart.xField || '',
                            font: { size: labelFontSize },
                            color: fontColor
                        }
                    },
                    y: {
                        ticks: {
                            font: { size: labelFontSize },
                            color: fontColor
                        },
                        title: {
                            display: !!chart.yField,
                            text: chart.yField || '',
                            font: { size: labelFontSize },
                            color: fontColor
                        }
                    }
                }
            }
        });
    }

    _calculateBubbleChartStatistics(chart, layer, rawFeatures) {
        let features = this._extractFeatures(rawFeatures, chart);
        let xField = chart.xField;
        let yField = chart.yField;
        let sizeField = chart.sizeField;
        let splitByField = chart.splitByField;
        let maxBubbleSize = parseInt(chart.maxBubbleSize, 10) || 20;
        let nullHandling = chart.nullHandling || 'skip';

        if (!layer.VisibleByDefault || features.length === 0) {
            this._updateBubbleChart(chart.id, []);
            return;
        }

        let maxSizeValue = 0;
        let groups = {};
        let labelMap = {};

        features.forEach(feature => {
            let x = parseFloat(feature.get(xField));
            let y = parseFloat(feature.get(yField));
            let size = sizeField ? parseFloat(feature.get(sizeField)) : 1;

            if (isNaN(x) || isNaN(y)) {
                if (nullHandling === 'zero') {
                    x = isNaN(x) ? 0 : x;
                    y = isNaN(y) ? 0 : y;
                } else {
                    return;
                }
            }
            if (isNaN(size)) size = 1;
            if (size > maxSizeValue) maxSizeValue = size;

            let groupKey = 'all';
            if (splitByField) {
                let splitVal = feature.get(splitByField);
                if (splitVal == null) splitVal = 'N/A';
                groupKey = String(splitVal);

                if (labelMap[groupKey] == null) {
                    let fkText = this._getFkText(feature, splitByField);
                    labelMap[groupKey] = fkText != null ? fkText : groupKey;
                }
            }

            if (!groups[groupKey]) groups[groupKey] = [];
            groups[groupKey].push({ x: x, y: y, _rawSize: size });
        });

        let groupKeys = this._sortKeys(Object.keys(groups), 'asc');
        let datasets = groupKeys.map(key => {
            let color = splitByField ? this._getColorForValue(key, chart) : this._getChartColors(chart)[0];
            let data = groups[key].map(p => ({
                x: p.x,
                y: p.y,
                r: maxSizeValue > 0 ? Math.max(2, (p._rawSize / maxSizeValue) * maxBubbleSize) : 5,
                _rawSize: p._rawSize
            }));

            return {
                label: splitByField ? (labelMap[key] || key) : chart.title || 'Data',
                data: data,
                backgroundColor: this._withAlpha(color, '80'),
                borderColor: color,
                borderWidth: 1
            };
        });

        this._updateBubbleChart(chart.id, datasets);
    }

    _updateBubbleChart(chartId, datasets) {
        let instance = this.chartInstances[chartId];
        if (instance) {
            instance.data.datasets = datasets;
            instance.update();
        }
    }

    _calculatePieChartStatistics(chart, layer, rawFeatures) {
        let features = this._extractFeatures(rawFeatures, chart);
        let categoryField = chart.categoryField;
        let valueField = chart.valueField;
        let maxSlices = parseInt(chart.maxSlices, 10) || 10;

        if (!layer.VisibleByDefault || features.length === 0) {
            this._updatePieChart(chart.id, [], []);
            return;
        }

        let groups = {};
        let labelMap = {};

        features.forEach(feature => {
            let key = feature.get(categoryField);
            if (key == null) key = 'N/A';
            let keyStr = String(key);

            if (labelMap[keyStr] == null) {
                let fkText = this._getFkText(feature, categoryField);
                labelMap[keyStr] = fkText != null ? fkText : keyStr;
            }

            if (groups[keyStr] == null) {
                groups[keyStr] = [];
            }

            if (valueField != null && valueField.trim() !== '') {
                groups[keyStr].push(feature.get(valueField));
            } else {
                groups[keyStr].push(1);
            }
        });

        let entries = Object.keys(groups).map(key => {
            let items = groups[key];
            let value;

            if (valueField == null || valueField.trim() === '') {
                value = items.length;
            } else {
                switch (chart.aggregation) {
                    case 'SUM': value = this._getSumResult(items); break;
                    case 'AVERAGE': value = this._getAverageResult(items); break;
                    case 'MIN': value = this._getMinResult(items); break;
                    case 'MAX': value = this._getMaxResult(items); break;
                    case 'MEDIAN': value = this._getMedianResult(items); break;
                    case 'COUNT_UNIQUE': value = this._getUniqueCountResult(items); break;
                    case 'COUNT': value = this._getNonNullCount(items); break;
                    default: value = this._getNonNullCount(items); break;
                }
            }

            return { key: key, label: labelMap[key], value: value };
        });

        let sortedKeys = this._sortKeys(entries.map(e => e.key), 'asc');
        let keyOrder = {};
        sortedKeys.forEach((k, i) => { keyOrder[k] = i; });
        entries.sort((a, b) => keyOrder[a.key] - keyOrder[b.key]);

        if (entries.length > maxSlices) {
            let byValue = entries.slice().sort((a, b) => (b.value || 0) - (a.value || 0));
            let keepKeys = new Set(byValue.slice(0, maxSlices - 1).map(e => e.key));
            let kept = entries.filter(e => keepKeys.has(e.key));
            let dropped = entries.filter(e => !keepKeys.has(e.key));
            let otherValue = dropped.reduce((sum, e) => sum + (e.value || 0), 0);
            kept.push({ key: '__other__', label: 'Other', value: otherValue });
            entries = kept;
        }

        let labels = entries.map(e => e.label);
        let keys = entries.map(e => e.key);
        let data = entries.map(e => e.value);
        let colors = keys.map(k => this._getColorForValue(k, chart));
        this._updatePieChart(chart.id, labels, data, colors);
    }

    _updatePieChart(chartId, labels, data, colors) {
        let instance = this.chartInstances[chartId];
        if (instance) {
            instance.data.labels = labels;
            instance.data.datasets[0].data = data;
            if (colors) {
                instance.data.datasets[0].backgroundColor = colors;
            }
            instance.update();
        }
    }

    _getChartWidth(chart) {
        let spec = this._chartTypes[chart.type];
        return spec ? spec.defaultWidth : 200;
    }

    _getChartHeight(chart) {
        let spec = this._chartTypes[chart.type];
        return spec ? spec.defaultHeight : 200;
    }

    _ensureStyles() {
        if (document.getElementById('xp-chart-panel-styles')) return;

        let style = document.createElement('style');
        style.id = 'xp-chart-panel-styles';
        style.textContent =
            '.xp-chart-panel {' +
                'position:absolute;z-index:500;background:#fff;border:1px solid #ccc;border-radius:4px;' +
                'box-shadow:0 2px 8px rgba(0,0,0,.15);display:flex;flex-direction:column;overflow:hidden;' +
            '}' +
            '.xp-chart-panel-dragbar {' +
                'min-height:24px;background:var(--theme-primary-700);cursor:move;user-select:none;flex-shrink:0;' +
                'display:flex;align-items:center;padding:0 6px 0 10px;gap:6px;' +
            '}' +
            '.xp-chart-panel-title {' +
                'flex:1;color:#fff;font-weight:600;line-height:1.2;font-size:12px;letter-spacing:0.02em;' +
                'text-align:center;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;' +
            '}' +
            '.xp-grid-chart-export-btn {' +
                'flex-shrink:0;width:22px;height:18px;padding:0;cursor:pointer;' +
                'border:1px solid rgba(255,255,255,0.35);background:rgba(255,255,255,0.12);' +
                'border-radius:3px;color:#fff;display:flex;align-items:center;justify-content:center;' +
                'transition:background 0.15s ease,border-color 0.15s ease;' +
            '}' +
            '.xp-grid-chart-export-btn:hover {background:rgba(255,255,255,0.28);border-color:rgba(255,255,255,0.6);}' +
            '.xp-grid-chart-export-btn svg {width:12px;height:12px;display:block;}' +
            '.xp-chart-panel-body {' +
                'flex:1;overflow:hidden;display:flex;flex-direction:column;' +
            '}' +
            '.xp-chart-card-container {' +
                'display:flex;flex-direction:column;height:100%;padding:0;' +
            '}' +
            '.xp-chart-card-content-wrapper {' +
                'flex:1;display:flex;align-items:center;justify-content:center;' +
                'min-height:0;padding:4px;text-align:center;' +
            '}' +
            '.xp-chart-card-container .card-chart-content {' +
                'margin:0;font-weight:bold;line-height:1;' +
            '}' +
            '.xp-chart-card-grid {' +
                'display:grid;grid-template-columns:repeat(auto-fit,minmax(60px,1fr));' +
                'grid-auto-rows:1fr;gap:4px;flex:1;min-height:0;padding:4px;overflow:auto;' +
            '}' +
            '.xp-chart-card-tile {' +
                'display:flex;flex-direction:column;align-items:center;justify-content:center;' +
                'padding:4px;border-radius:3px;text-align:center;overflow:hidden;min-height:0;' +
            '}' +
            '.xp-chart-card-tile-value {' +
                'margin:0;font-weight:bold;line-height:1.1;' +
            '}' +
            '.xp-chart-card-tile-label {' +
                'margin:2px 0 0 0;opacity:0.85;line-height:1.1;word-break:break-word;' +
            '}' +
            '.xp-chart-panel-resize {' +
                'position:absolute;right:1px;bottom:1px;width:12px;height:12px;cursor:se-resize;' +
                'color:var(--theme-primary-700);font-size:1rem;line-height:12px;text-align:center;' +
                'user-select:none;opacity:0.6;' +
            '}' +
            '.xp-chart-panel-resize:hover {opacity:1;}' +
            '.xp-chart-pie-canvas-wrapper {' +
                'flex:1;min-height:0;position:relative;' +
            '}' +
            '.xp-grid-chart-host {' +
                'flex:1;min-height:0;display:flex;' +
            '}' +
            '.xp-grid-chart {' +
                'flex:1;display:flex;flex-direction:column;min-height:0;min-width:0;' +
                'font-size:12px;color:#333;background:#fff;' +
            '}' +
            '.xp-grid-chart.is-comfortable {font-size:13px;}' +
            '.xp-grid-chart-section {min-width:0;}' +
            '.xp-grid-chart-section-header, .xp-grid-chart-section-bottom {flex-shrink:0;overflow:hidden;}' +
            '.xp-grid-chart-section-body {flex:1;min-height:0;overflow:auto;background:#fff;}' +
            '.xp-grid-chart-table {' +
                'width:100%;border-collapse:separate;border-spacing:0;table-layout:auto;' +
            '}' +
            '.xp-grid-chart-section-header .xp-grid-chart-table thead th {' +
                'background:#f3f4f7;color:var(--theme-primary-700);' +
                'font-weight:600;text-align:left;padding:6px 10px;cursor:pointer;user-select:none;' +
                'white-space:nowrap;font-size:11px;text-transform:uppercase;letter-spacing:0.04em;' +
                'border-bottom:2px solid var(--theme-primary-700);' +
            '}' +
            '.xp-grid-chart.is-comfortable .xp-grid-chart-section-header .xp-grid-chart-table thead th {padding:8px 12px;font-size:12px;}' +
            '.xp-grid-chart-section-header .xp-grid-chart-table thead th:hover {background:#e9ebf2;}' +
            '.xp-grid-chart-section-header .xp-grid-chart-table thead th.is-sorted {background:#e9ebf2;}' +
            '.xp-grid-chart-th-arrow {display:inline-block;min-width:10px;margin-left:6px;opacity:0.9;font-size:9px;}' +
            '.xp-grid-chart-section-body .xp-grid-chart-table tbody td {' +
                'padding:4px 10px;border-bottom:1px solid #eef0f3;white-space:nowrap;' +
                'overflow:hidden;text-overflow:ellipsis;max-width:260px;' +
            '}' +
            '.xp-grid-chart.is-comfortable .xp-grid-chart-section-body .xp-grid-chart-table tbody td {padding:7px 12px;}' +
            '.xp-grid-chart-section-body .xp-grid-chart-table tbody tr:nth-child(even) td {background:#f7f8fb;}' +
            '.xp-grid-chart-section-body .xp-grid-chart-table tbody tr.is-clickable {cursor:pointer;}' +
            '.xp-grid-chart-section-body .xp-grid-chart-table tbody tr:hover td {' +
                'background:color-mix(in srgb, var(--theme-primary-700) 10%, transparent);' +
            '}' +
            '.xp-grid-chart-section-body .xp-grid-chart-table tbody tr:active td {' +
                'background:color-mix(in srgb, var(--theme-primary-700) 18%, transparent);' +
            '}' +
            '.xp-grid-chart-section-body .xp-grid-chart-table tbody td.is-numeric {text-align:right;font-variant-numeric:tabular-nums;}' +
            '.xp-grid-chart-section-body .xp-grid-chart-table tbody td.is-empty {color:#bbb;}' +
            '.xp-grid-chart-chip {' +
                'display:inline-block;padding:1px 8px;border-radius:10px;' +
                'background:color-mix(in srgb, var(--theme-primary-700) 12%, transparent);' +
                'color:var(--theme-primary-700);font-size:11px;font-weight:500;' +
                'line-height:1.5;white-space:nowrap;max-width:100%;' +
                'overflow:hidden;text-overflow:ellipsis;vertical-align:middle;' +
            '}' +
            '.xp-grid-chart-section-body .xp-grid-chart-table tbody td.has-bar {position:relative;overflow:hidden;}' +
            '.xp-grid-chart-bar-wrap {position:relative;display:block;width:100%;min-height:16px;}' +
            '.xp-grid-chart-bar {' +
                'position:absolute;left:0;top:50%;transform:translateY(-50%);' +
                'height:calc(100% + 6px);border-radius:2px;pointer-events:none;transition:width 0.2s ease;' +
            '}' +
            '.xp-grid-chart-bar-theme {background:color-mix(in srgb, var(--theme-primary-700) 25%, transparent);}' +
            '.xp-grid-chart-bar-green {background:rgba(92,184,92,0.30);}' +
            '.xp-grid-chart-bar-blue {background:rgba(91,192,222,0.30);}' +
            '.xp-grid-chart-bar-value {position:relative;z-index:1;font-variant-numeric:tabular-nums;}' +
            '.xp-grid-chart-section-body .xp-grid-chart-table tbody td.has-icon {white-space:nowrap;}' +
            '.xp-grid-chart-icon {display:inline-block;margin-right:6px;vertical-align:middle;}' +
            '.xp-grid-chart-icon-traffic {' +
                'width:10px;height:10px;border-radius:50%;box-shadow:inset 0 0 0 1px rgba(0,0,0,0.08);' +
            '}' +
            '.xp-grid-chart-icon-traffic.is-low {background:#d9534f;}' +
            '.xp-grid-chart-icon-traffic.is-mid {background:#f0ad4e;}' +
            '.xp-grid-chart-icon-traffic.is-high {background:#5cb85c;}' +
            '.xp-grid-chart-icon-arrows, .xp-grid-chart-icon-check {width:14px;text-align:center;font-weight:700;line-height:1;}' +
            '.xp-grid-chart-icon-arrows.is-low, .xp-grid-chart-icon-check.is-low {color:#d9534f;}' +
            '.xp-grid-chart-icon-arrows.is-mid, .xp-grid-chart-icon-check.is-mid {color:#999;}' +
            '.xp-grid-chart-icon-arrows.is-high, .xp-grid-chart-icon-check.is-high {color:#5cb85c;}' +
            '.xp-grid-chart-icon-value {font-variant-numeric:tabular-nums;vertical-align:middle;}' +
            '.xp-grid-chart-empty {text-align:center;color:#999;padding:16px 8px;font-style:italic;}' +
            '.xp-grid-chart-section-bottom {' +
                'border-top:1px solid #e3e6ec;background:#fafbfd;' +
            '}' +
            '.xp-grid-chart-section-bottom .xp-grid-chart-table tfoot td {' +
                'padding:5px 10px;font-weight:600;color:#333;background:transparent;' +
                'border-bottom:1px solid #eef0f3;' +
            '}' +
            '.xp-grid-chart-section-bottom .xp-grid-chart-table tfoot td.is-numeric {text-align:right;font-variant-numeric:tabular-nums;}' +
            '.xp-grid-chart-section-body .xp-grid-chart-table tbody tr.xp-grid-chart-group-header td {' +
                'background:color-mix(in srgb, var(--theme-primary-700) 8%, transparent);cursor:pointer;' +
                'border-top:1px solid color-mix(in srgb, var(--theme-primary-700) 22%, transparent);' +
                'border-bottom:1px solid color-mix(in srgb, var(--theme-primary-700) 12%, transparent);' +
                'padding:4px 10px;font-weight:600;color:#333;user-select:none;' +
            '}' +
            '.xp-grid-chart-section-body .xp-grid-chart-table tbody tr.xp-grid-chart-group-header:hover td {' +
                'background:color-mix(in srgb, var(--theme-primary-700) 14%, transparent);' +
            '}' +
            '.xp-grid-chart-section-body .xp-grid-chart-table tbody tr.xp-grid-chart-group-header td.is-numeric {text-align:right;font-variant-numeric:tabular-nums;}' +
            '.xp-grid-chart-group-arrow {' +
                'display:inline-block;width:12px;color:var(--theme-primary-700);font-size:10px;margin-right:4px;' +
            '}' +
            '.xp-grid-chart-group-label {color:var(--theme-primary-700);}' +
            '.xp-grid-chart-group-count {color:#888;margin-left:6px;font-weight:400;font-size:11px;}' +
            '.xp-grid-chart-total-kind {' +
                'display:inline-block;margin-right:6px;padding:2px 7px;border-radius:3px;' +
                'background:color-mix(in srgb, var(--theme-primary-700) 14%, transparent);color:var(--theme-primary-700);' +
                'font-size:9px;text-transform:uppercase;letter-spacing:0.06em;font-weight:700;vertical-align:middle;' +
            '}' +
            '.xp-grid-chart-pager {' +
                'display:flex;align-items:center;justify-content:center;gap:6px;' +
                'padding:4px 8px;font-size:11px;color:#666;' +
            '}' +
            '.xp-grid-chart-page-btn {' +
                'border:none;background:transparent;border-radius:3px;cursor:pointer;' +
                'width:22px;height:22px;line-height:1;padding:0;font-size:14px;color:#888;' +
                'transition:background 0.15s ease,color 0.15s ease;' +
            '}' +
            '.xp-grid-chart-page-btn:hover:not(:disabled) {' +
                'background:color-mix(in srgb, var(--theme-primary-700) 12%, transparent);color:var(--theme-primary-700);' +
            '}' +
            '.xp-grid-chart-page-btn:disabled {opacity:0.3;cursor:default;}' +
            '.xp-grid-chart-page-info {font-variant-numeric:tabular-nums;padding:0 6px;}' +
            '.xp-grid-chart-th {position:relative;}' +
            '.xp-grid-chart-th + .xp-grid-chart-th {border-left:1px solid #e3e6ec;}' +
            '.xp-grid-chart-th-grip {' +
                'position:absolute;top:4px;right:0;width:6px;height:calc(100% - 8px);' +
                'cursor:col-resize;user-select:none;background:transparent;z-index:2;' +
                'border-right:2px solid transparent;transition:border-color 0.15s ease;' +
            '}' +
            '.xp-grid-chart-th-grip:hover {border-right-color:var(--theme-primary-700);}' +
            'body.xp-grid-chart-resizing, body.xp-grid-chart-resizing * {cursor:col-resize !important;user-select:none !important;}' +
            '.xp-grid-chart.is-sticky-first .xp-grid-chart-table thead th:first-child,' +
            '.xp-grid-chart.is-sticky-first .xp-grid-chart-table tbody td:first-child,' +
            '.xp-grid-chart.is-sticky-first .xp-grid-chart-table tfoot td:first-child {' +
                'position:sticky;left:0;z-index:3;' +
            '}' +
            '.xp-grid-chart.is-sticky-first .xp-grid-chart-table thead th:first-child {z-index:4;background:#f3f4f7;}' +
            '.xp-grid-chart.is-sticky-first .xp-grid-chart-section-body .xp-grid-chart-table tbody td:first-child {' +
                'background:#fff;box-shadow:1px 0 0 #eef0f3;' +
            '}' +
            '.xp-grid-chart.is-sticky-first .xp-grid-chart-section-body .xp-grid-chart-table tbody tr:nth-child(even) td:first-child {background:#f7f8fb;}' +
            '.xp-grid-chart.is-sticky-first .xp-grid-chart-section-body .xp-grid-chart-table tbody tr:hover td:first-child {' +
                'background:color-mix(in srgb, var(--theme-primary-700) 10%, transparent);' +
            '}' +
            '.xp-grid-chart.is-sticky-first .xp-grid-chart-section-body .xp-grid-chart-table tbody tr.xp-grid-chart-group-header td:first-child {' +
                'background:color-mix(in srgb, var(--theme-primary-700) 8%, transparent);' +
            '}' +
            '.xp-grid-chart.is-sticky-first .xp-grid-chart-section-bottom .xp-grid-chart-table tfoot td:first-child {background:#fafbfd;box-shadow:1px 0 0 #e3e6ec;}' +
            '.xp-grid-chart-section-body .xp-grid-chart-table tbody td.has-spark {padding:2px 6px;}' +
            '.xp-grid-chart-spark {display:inline-block;width:80px;height:18px;vertical-align:middle;}' +
            '.xp-grid-chart-spark-line {' +
                'fill:none;stroke:var(--theme-primary-700);stroke-width:1.2;' +
                'stroke-linecap:round;stroke-linejoin:round;' +
            '}' +
            '.xp-grid-chart-spark-dot {fill:var(--theme-primary-700);}';

        document.head.appendChild(style);
    }

    _bringToFront(panel) {
        panel.style.zIndex = String(this._nextZ++);
    }

    _clampPosition(left, top, width, height) {
        const minVisible = 40;
        const dragbar = 20;
        const bounds = this._getHostBounds();
        let maxLeft = bounds.width - minVisible;
        let minLeft = -(width - minVisible);
        let maxTop = bounds.height - dragbar;
        let minTop = 0;
        return {
            left: Math.max(minLeft, Math.min(maxLeft, left)),
            top: Math.max(minTop, Math.min(maxTop, top))
        };
    }

    _makeDraggable(panel, chartId) {
        let titleBar = panel.querySelector('.xp-chart-panel-dragbar');
        let startX, startY, startLeft, startTop;

        let onMouseMove = (e) => {
            let clamped = this._clampPosition(
                startLeft + e.clientX - startX,
                startTop + e.clientY - startY,
                panel.offsetWidth,
                panel.offsetHeight
            );
            panel.style.left = clamped.left + 'px';
            panel.style.top = clamped.top + 'px';
        };

        let onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            this._savePosition(chartId, panel);
        };

        titleBar.addEventListener('mousedown', (e) => {
            e.preventDefault();
            startX = e.clientX;
            startY = e.clientY;
            startLeft = panel.offsetLeft;
            startTop = panel.offsetTop;
            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp);
        });
    }

    _makeResizable(panel, chartId) {
        let handle = panel.querySelector('.xp-chart-panel-resize');
        let startX, startY, startW, startH;
        const minW = 80, minH = 60;

        let onMouseMove = (e) => {
            panel.style.width = Math.max(minW, startW + e.clientX - startX) + 'px';
            panel.style.height = Math.max(minH, startH + e.clientY - startY) + 'px';
        };

        let onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            this._savePosition(chartId, panel);
        };

        handle.addEventListener('mousedown', (e) => {
            e.preventDefault();
            startX = e.clientX;
            startY = e.clientY;
            startW = panel.offsetWidth;
            startH = panel.offsetHeight;
            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp);
        });
    }

    _getStorageKey() {
        if (typeof xapp !== 'undefined' && xapp != null && xapp.currentApp != null) {
            return 'mapChartPositions_' + xapp.currentApp + '_' + this.mapId;
        }
        return 'mapChartPositions__' + this.mapId;
    }

    _getSavedPositions() {
        try {
            let key = this._getStorageKey();
            let data = localStorage.getItem(key);

            if (!data) {
                let fallbackKey = 'mapChartPositions__' + this.mapId;
                if (fallbackKey !== key) {
                    data = localStorage.getItem(fallbackKey);
                }
            }

            return data ? JSON.parse(data) : {};
        } catch (e) {
            return {};
        }
    }

    _getSavedPosition(chartId) {
        let positions = this._getSavedPositions();
        return positions[chartId] || null;
    }

    _savePosition(chartId, panel) {
        let positions = this._getSavedPositions();
        positions[chartId] = {
            top: panel.offsetTop,
            left: panel.offsetLeft,
            width: panel.offsetWidth,
            height: panel.offsetHeight
        };
        localStorage.setItem(this._getStorageKey(), JSON.stringify(positions));
    }

    _removeSavedPosition(chartId) {
        let positions = this._getSavedPositions();
        delete positions[chartId];
        localStorage.setItem(this._getStorageKey(), JSON.stringify(positions));
    }

    _addEventHandlers() {
        $event.register(eventTypes.MAP_RENDERED, this._getMapRenderedHandler(), this, this.classId);
        $event.register(eventTypes.CHART_ADDED, this._getChartAddedHandler(), this, this.classId);
        $event.register(eventTypes.CHART_REMOVED, this._getChartRemovedHandler(), this, this.classId);
        $event.register(eventTypes.CHART_UPDATED, this._getChartUpdatedHandler(), this, this.classId);
        $event.register(eventTypes.LAYER_REMOVED, this._getLayerRemovedHandler(), this, this.classId);
    }

    _getLayerRemovedHandler() {
        return (eventName, data, sender) => {
            let ids = (data && Array.isArray(data.ids)) ? data.ids : [];
            ids.forEach(layerId => {
                let layer = this.chartLayers[layerId];
                if (layer == null) return;
                let chartIds = layer.Charts ? Object.keys(layer.Charts) : [];
                chartIds.forEach(chartId => {
                    this._disposeChart(chartId, layerId);
                    this._removeSavedPosition(chartId);
                });
                delete this.chartLayers[layerId];
            });
        };
    }

    _getMapRenderedHandler() {
        let timeoutId;

        const handler = (eventName, data, sender) => {
            clearTimeout(timeoutId);

            timeoutId = setTimeout(() => {
                this._resolvePendingAnchor();
                if (this.chartLayers != null) {
                    let layerIds = Object.keys(this.chartLayers);

                    if (layerIds != null && layerIds.length > 0) {
                        let geometry = fromExtent(this.mapControl.getViewExtent());

                        layerIds.forEach((layerId) => {
                            let layer = this.chartLayers[layerId];
                            let chartIds = layer.Charts ? Object.keys(layer.Charts) : [];
                            if (chartIds.length === 0) return;

                            let features = this.mapControl.selectionHandler.getFeaturesInGeometry(geometry, layerId);
                            chartIds.forEach(chartId => {
                                this._calculateChartStatistics(layer.Charts[chartId], layer, features);
                            });
                        });
                    }
                }
            }, 200);
        };

        return handler;
    }

    _getFeaturesHash(features) {
        if (features == null || features.length === 0) return '0';
        let ids = [];
        for (let i = 0; i < features.length; i++) {
            let id = features[i].getId() || features[i].ol_uid;
            ids.push(id);
        }
        return features.length + ':' + ids.join(',');
    }

    _calculateChartStatistics(chart, layer, features) {
        let hash = this._getFeaturesHash(features);
        let hashKey = layer.Id + '_' + chart.id;
        if (this._lastDataHash[hashKey] === hash) return;
        this._lastDataHash[hashKey] = hash;

        let spec = this._chartTypes[chart.type];
        if (spec) spec.calc(chart, layer, features);
    }

    _calculateCardChartStatistics(chart, layer, rawFeatures) {
        let features = this._extractFeatures(rawFeatures, chart);

        if (chart.categoryField) {
            this._renderCategorizedCard(chart, layer, features);
            return;
        }

        let target = document.getElementById('card-chart-content-' + chart.id);
        if (!target) return;

        if (!layer.VisibleByDefault) {
            target.innerText = 'N/A';
            return;
        }

        target.innerText = formatNumber(this._aggregateForCard(features, chart.field, chart.aggregation));
    }

    _aggregateForCard(features, fieldName, aggregation) {
        if (aggregation === 'COUNT' || aggregation == null) return features.length;
        let items = features.map(f => f.get(fieldName));
        switch (aggregation) {
            case 'COUNT_UNIQUE': return this._getUniqueCountResult(items);
            case 'SUM': return this._getSumResult(items);
            case 'AVERAGE': return this._getAverageResult(items);
            case 'MIN': return this._getMinResult(items);
            case 'MAX': return this._getMaxResult(items);
            case 'MEDIAN': return this._getMedianResult(items);
            default: return features.length;
        }
    }

    _renderCategorizedCard(chart, layer, features) {
        let grid = document.getElementById('card-chart-grid-' + chart.id);
        if (!grid) return;
        grid.textContent = '';
        this._applyCardsPerRow(grid, chart.cardsPerRow);

        if (!layer.VisibleByDefault || features.length === 0) return;

        let categoryField = chart.categoryField;
        let sortOrder = chart.sortOrder || 'asc';
        let maxCategories = parseInt(chart.maxCategories, 10) || 10;
        let valueFontSize = parseInt(chart.valueFontSize, 10) || 20;
        let labelFontSize = parseInt(chart.titleFontSize, 10) || 10;

        let grouped = this._groupByX(features, categoryField, sortOrder);
        let entries = grouped.xKeys.map((key, i) => ({
            key: key,
            label: grouped.labels[i],
            value: this._aggregateForCard(grouped.featuresByX[key], chart.field, chart.aggregation)
        }));

        if (entries.length > maxCategories) {
            let order = {};
            grouped.xKeys.forEach((k, i) => { order[k] = i; });
            let topKeys = new Set(
                entries.slice().sort((a, b) => (Number(b.value) || 0) - (Number(a.value) || 0))
                    .slice(0, maxCategories).map(e => e.key)
            );
            entries = entries.filter(e => topKeys.has(e.key))
                .sort((a, b) => order[a.key] - order[b.key]);
        }

        entries.forEach(entry => {
            let bg = this._getColorForValue(entry.key, chart);
            let fg = this._getContrastColor(bg);

            let tile = document.createElement('div');
            tile.className = 'xp-chart-card-tile';
            tile.style.background = bg;
            tile.style.color = fg;

            let valueEl = document.createElement('p');
            valueEl.className = 'xp-chart-card-tile-value';
            valueEl.style.fontSize = valueFontSize + 'px';
            valueEl.textContent = entry.value != null ? formatNumber(entry.value) : '';

            let labelEl = document.createElement('p');
            labelEl.className = 'xp-chart-card-tile-label';
            labelEl.style.fontSize = labelFontSize + 'px';
            labelEl.textContent = entry.label;

            tile.appendChild(valueEl);
            tile.appendChild(labelEl);
            grid.appendChild(tile);
        });
    }

    _applyCardsPerRow(grid, cardsPerRow) {
        let n = parseInt(cardsPerRow, 10);
        grid.style.gridTemplateColumns = (n > 0) ? ('repeat(' + n + ', 1fr)') : '';
    }

    _getContrastColor(hex) {
        if (typeof hex !== 'string' || hex[0] !== '#') return '#000000';
        let h = hex.slice(1);
        if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
        if (h.length < 6) return '#000000';
        let r = parseInt(h.substr(0, 2), 16);
        let g = parseInt(h.substr(2, 2), 16);
        let b = parseInt(h.substr(4, 2), 16);
        let yiq = (r * 299 + g * 587 + b * 114) / 1000;
        return yiq >= 140 ? '#000000' : '#ffffff';
    }

    _toNumbers(items) {
        if (!Array.isArray(items)) return [];
        let result = [];
        for (let i = 0; i < items.length; i++) {
            let v = items[i];
            if (v == null || v === '') continue;
            let n = typeof v === 'number' ? v : Number(v);
            if (Number.isFinite(n)) result.push(n);
        }
        return result;
    }

    _getUniqueCountResult(items) {
        if (!Array.isArray(items)) return 0;
        let set = new Set();
        for (let i = 0; i < items.length; i++) {
            let v = items[i];
            if (v == null) continue;
            set.add(v);
        }
        return set.size;
    }

    _getSumResult(items) {
        let nums = this._toNumbers(items);
        let sum = 0;
        for (let i = 0; i < nums.length; i++) sum += nums[i];
        return sum;
    }

    _getAverageResult(items) {
        let nums = this._toNumbers(items);
        if (nums.length === 0) return null;
        let sum = 0;
        for (let i = 0; i < nums.length; i++) sum += nums[i];
        return sum / nums.length;
    }

    _getMinResult(items) {
        let nums = this._toNumbers(items);
        if (nums.length === 0) return null;
        let min = nums[0];
        for (let i = 1; i < nums.length; i++) if (nums[i] < min) min = nums[i];
        return min;
    }

    _getMaxResult(items) {
        let nums = this._toNumbers(items);
        if (nums.length === 0) return null;
        let max = nums[0];
        for (let i = 1; i < nums.length; i++) if (nums[i] > max) max = nums[i];
        return max;
    }

    _getMedianResult(items) {
        let nums = this._toNumbers(items);
        if (nums.length === 0) return null;
        const sorted = nums.slice().sort((a, b) => a - b);
        const mid = Math.floor(sorted.length / 2);
        if (sorted.length % 2 !== 0) return sorted[mid];
        return (sorted[mid - 1] + sorted[mid]) / 2;
    }

    _getFkText(feature, fieldName) {
        return getFkText(feature, fieldName);
    }

    _extractFeatures(features, chart) {
        let result = [];

        features.forEach(feature => {
            if (feature.get('features') != null) {
                let clusterFeatures = feature.get('features');

                clusterFeatures.forEach(clusterFeature => {
                    result.push(clusterFeature);
                });
            } else {
                result.push(feature);
            }
        });

        let condition = this._getParsedCondition(chart);
        if (condition != null) {
            result = applyFilter(result, condition);
        }

        return result;
    }

    _getParsedCondition(chart) {
        if (chart.condition == null || chart.condition.trim() === '') return null;
        let cached = this._parsedConditions[chart.id];
        if (cached && cached.raw === chart.condition) return cached.parsed;
        let parsed;
        try {
            parsed = JSON.parse(chart.condition);
        } catch (e) {
            parsed = null;
        }
        this._parsedConditions[chart.id] = { raw: chart.condition, parsed: parsed };
        return parsed;
    }

    _getChartAddedHandler() {
        var handler = (eventName, data, sender) => {
            let layer = this.layerSettingsHandler.getLayerSettings(data.layerId);
            if (this.chartLayers[layer.Id] == null) {
                this.chartLayers[layer.Id] = layer;
            }

            this._initChart(layer, data.chart.id);
        };

        return handler;
    }

    _getChartUpdatedHandler() {
        var handler = (eventName, data, sender) => {
            let layer = this.layerSettingsHandler.getLayerSettings(data.layerId);
            if (this.chartLayers[layer.Id] == null) {
                this.chartLayers[layer.Id] = layer;
            }

            this._editChart(layer, data.chart.id);
            let hashKey = layer.Id + '_' + data.chart.id;
            delete this._lastDataHash[hashKey];
            delete this._parsedConditions[data.chart.id];
        };

        return handler;
    }

    _getChartRemovedHandler() {
        var handler = (eventName, data, sender) => {
            this._disposeChart(data.chartId, data.layerId);
            this._removeSavedPosition(data.chartId);
        };

        return handler;
    }

    _disposeChart(chartId, layerId) {
        if (this.chartInstances[chartId]) {
            this.chartInstances[chartId].destroy();
            delete this.chartInstances[chartId];
        }
        if (this._gridRenderers[chartId]) {
            delete this._gridRenderers[chartId];
        }
        let panel = document.getElementById(chartId);
        if (panel) panel.remove();
        delete this._parsedConditions[chartId];
        delete this._chartFingerprints[chartId];
        if (layerId != null) {
            delete this._lastDataHash[layerId + '_' + chartId];
        }
    }

}